import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organizationsApi } from '../../services/api';
import { Organization, OrganizationCreate, OrganizationUpdate, PaginatedResponse } from '../../types';

interface OrganizationsState {
  organizations: PaginatedResponse<Organization> | null;
  selectedOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: OrganizationsState = {
  organizations: null,
  selectedOrganization: null,
  isLoading: false,
  error: null,
};

export const fetchOrganizations = createAsyncThunk(
  'organizations/fetchOrganizations',
  async (params: { skip?: number; limit?: number; search?: string; status?: string }, { rejectWithValue }) => {
    try {
      const response = await organizationsApi.getOrganizations(params);
      const sortedData = [...response.data.data].sort((a, b) => a.org_id - b.org_id);
      return {
        items: sortedData,
        total: response.data.meta.total,
        page: response.data.meta.page,
        size: response.data.meta.per_page,
        pages: response.data.meta.pages
      };
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch organizations');
    }
  }
);

export const createOrganization = createAsyncThunk(
  'organizations/createOrganization',
  async (data: OrganizationCreate, { rejectWithValue }) => {
    try {
      const response = await organizationsApi.createOrganization(data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to create organization');
    }
  }
);

export const updateOrganization = createAsyncThunk(
  'organizations/updateOrganization',
  async ({ id, data }: { id: number; data: OrganizationUpdate }, { rejectWithValue }) => {
    try {
      const response = await organizationsApi.updateOrganization(id, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to update organization');
    }
  }
);

export const deleteOrganization = createAsyncThunk(
  'organizations/deleteOrganization',
  async (orgId: number, { rejectWithValue }) => {
    try {
      await organizationsApi.updateOrganization(orgId, { status: 'deleted' });
      return orgId;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to delete organization');
    }
  }
);

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    setSelectedOrganization: (state, action) => {
      state.selectedOrganization = action.payload;
    },
    clearSelectedOrganization: (state) => {
      state.selectedOrganization = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations = action.payload;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.organizations?.items) {
          const newItems = [...state.organizations.items, action.payload]
            .sort((a, b) => a.org_id - b.org_id);
          state.organizations.items = newItems;
          state.organizations.total += 1;
        }
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.organizations?.items) {
          const index = state.organizations.items.findIndex(org => org.org_id === action.payload.org_id);
          if (index !== -1) {
            state.organizations.items[index] = action.payload;
          }
        }
        if (state.selectedOrganization?.org_id === action.payload.org_id) {
          state.selectedOrganization = action.payload;
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.organizations?.items) {
          state.organizations.items = state.organizations.items
            .filter(org => org.org_id !== action.payload)
            .sort((a, b) => a.org_id - b.org_id);
          state.organizations.total -= 1;
        }
        if (state.selectedOrganization?.org_id === action.payload) {
          state.selectedOrganization = null;
        }
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedOrganization, clearSelectedOrganization, clearError } = organizationsSlice.actions;

export default organizationsSlice.reducer;